import { z } from 'zod';

/**
 * Identité d'un montage canvas — vocabulaire commun aux DEUX formes que le
 * protocole accepte pendant le retrait de `documentSessionId` (#1335, phase
 * « expand » de la spec #1327).
 *
 * - **legacy** — `documentSessionId`, l'identifiant que le navigateur tire de
 *   son montage, éventuellement accompagné de `mountEpoch`. Les clients déjà
 *   ouverts au moment du déploiement retrouvent encore LEUR portée par cette
 *   valeur : c'est elle qui adresse les commandes qu'on leur envoie. La rendre
 *   optionnelle et cesser de la lire ne suffirait donc pas — le navigateur ne
 *   saurait plus à quel document la commande s'adresse.
 * - **conversation** — `(conversationId, mountEpoch)`, l'identité cible. Le
 *   couple est indissociable : l'époque seule ne désigne rien (deux
 *   conversations peuvent porter la même valeur, l'autorité qui l'alloue est
 *   monotone au niveau du runtime), et la conversation seule ne distingue pas
 *   deux montages successifs du même fil.
 *
 * ⚠️ **Aucun alias.** Une trame qui porte les deux formes est REFUSÉE, jamais
 * « tolérée en préférant la nouvelle » : accepter les deux laisserait passer un
 * `documentSessionId` rempli avec le `conversationId` — ce que la spec interdit
 * explicitement — et rendrait indétectable une divergence entre les deux
 * valeurs.
 *
 * Critère de retrait de la forme legacy : voir `docs/features/redaction-web-canvas/README.md`,
 * section « Retrait du protocole (#1335 → #1336 → #1339) ».
 */
export type CanvasMountIdentity =
  | { forme: 'legacy'; documentSessionId: string; mountEpoch?: number }
  | { forme: 'conversation'; conversationId: string; mountEpoch: number };

/**
 * Les deux champs Zod de la forme CONVERSATION, à greffer sur une trame par
 * `.extend()` après avoir retiré `documentSessionId` de sa version legacy.
 *
 * Une forme partagée plutôt que deux littéraux recopiés : les enveloppes et les
 * reçus de transition (`actionCatalog.ts`) sont tous construits par
 * `omit` + `extend` sur leur schéma legacy, et un champ ajouté ici ne peut donc
 * pas manquer à l'un d'eux.
 *
 * ⚠️ Les deux clés sont **requises**, et c'est le contrat : la conversation
 * seule ne désigne pas un montage — deux montages successifs du même fil la
 * partagent. Il n'existe volontairement PAS de pendant `…LegacyShape` : la
 * forme legacy n'est jamais greffée, elle est le point de départ dont on part.
 */
export const CanvasConversationMountIdentityShape = {
  conversationId: z.string().uuid(),
  mountEpoch: z.number().int().nonnegative(),
} as const;

/**
 * Ce qu'une trame a effectivement annoncé comme identité de montage.
 *
 * Quatre issues distinctes, et non un `null` fourre-tout : un champ ABSENT ne
 * dit pas la même chose qu'un champ REFUSÉ. « absente » autorise un chemin de
 * compatibilité (un hôte plus ancien qui ne publie rien), « aliasee » et
 * « epoque_manquante » sont des trames malformées qu'il faut refuser en
 * nommant leur défaut. Les replier ensemble ferait traiter un client sain
 * comme un client fautif, ou l'inverse.
 */
export type LectureIdentiteDeMontage =
  | { statut: 'lue'; identite: CanvasMountIdentity }
  | { statut: 'absente' }
  | { statut: 'aliasee' }
  | { statut: 'epoque_manquante' };

/**
 * Lit l'identité de montage posée sur une trame, sans jamais deviner.
 *
 * @param source les champs d'identité tels qu'ils arrivent du fil.
 */
export function lireIdentiteDeMontage(source: {
  documentSessionId?: string | null;
  conversationId?: string | null;
  mountEpoch?: number | null;
}): LectureIdentiteDeMontage {
  const documentSessionId = source.documentSessionId ?? undefined;
  const conversationId = source.conversationId ?? undefined;
  const mountEpoch = source.mountEpoch ?? undefined;

  if (documentSessionId !== undefined && conversationId !== undefined) return { statut: 'aliasee' };
  if (documentSessionId !== undefined) {
    return {
      statut: 'lue',
      identite: {
        forme: 'legacy',
        documentSessionId,
        ...(mountEpoch !== undefined ? { mountEpoch } : {}),
      },
    };
  }
  if (conversationId !== undefined) {
    if (mountEpoch === undefined) return { statut: 'epoque_manquante' };
    return { statut: 'lue', identite: { forme: 'conversation', conversationId, mountEpoch } };
  }
  return { statut: 'absente' };
}

/**
 * Rend une identité lisible dans un message d'erreur lu hors contexte.
 *
 * `mountEpoch=non-annoncée` plutôt qu'un champ omis : le lecteur doit pouvoir
 * distinguer « ce client n'annonce pas d'époque » de « l'époque vaut 0 ».
 */
export function decrireIdentiteDeMontage(identite: CanvasMountIdentity): string {
  switch (identite.forme) {
    case 'legacy':
      return `documentSessionId=${identite.documentSessionId} mountEpoch=${identite.mountEpoch ?? 'non-annoncée'}`;
    case 'conversation':
      return `conversationId=${identite.conversationId} mountEpoch=${identite.mountEpoch}`;
  }
}

/**
 * Refine Zod qui impose EXACTEMENT une forme d'identité de montage sur une
 * trame (#1335).
 *
 * Préféré à une `z.union` de deux objets `.strict()` sur les trames du
 * protocole Case Discovery, pour une raison de compatibilité : ces trames sont
 * aujourd'hui des `z.object` non stricts, qui RETIRENT les clés inconnues. Les
 * passer en strict refuserait d'un coup tout client qui pose un champ que ce
 * backend ne connaît pas encore — l'inverse de ce que la phase « expand »
 * cherche. Ce refine ferme l'alias sans durcir le reste de la trame.
 *
 * @param options.identiteFacultative une trame sans aucune identité est
 * acceptée (l'appelant Word de `redaction_sse_complete` n'en pose aucune).
 * @param options.epoqueObligatoireEnLegacy la forme legacy doit elle aussi
 * porter `mountEpoch`. Sert à NE PAS relâcher une validation que le protocole
 * legacy imposait déjà, comme sur `canvas_document_state`.
 */
export function verifierFormeDIdentiteDeMontage(
  source: { documentSessionId?: string; conversationId?: string; mountEpoch?: number },
  ctx: z.RefinementCtx,
  options: { identiteFacultative?: boolean; epoqueObligatoireEnLegacy?: boolean } = {},
): void {
  const lecture = lireIdentiteDeMontage(source);
  switch (lecture.statut) {
    case 'lue':
      if (
        options.epoqueObligatoireEnLegacy
        && lecture.identite.forme === 'legacy'
        && lecture.identite.mountEpoch === undefined
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['mountEpoch'],
          message: 'mountEpoch is required alongside documentSessionId on this frame',
        });
      }
      return;
    case 'aliasee':
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['conversationId'],
        message:
          'a canvas mount announces exactly one identity: documentSessionId (legacy) OR conversationId + mountEpoch. '
          + 'Sending both aliases them and hides a divergence between the two values',
      });
      return;
    case 'epoque_manquante':
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['mountEpoch'],
        message: 'mountEpoch is required alongside conversationId: the conversation alone does not identify a mount',
      });
      return;
    case 'absente':
      if (options.identiteFacultative) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['documentSessionId'],
        message: 'a canvas mount identity is required: documentSessionId (legacy) or conversationId + mountEpoch',
      });
  }
}

/**
 * Clé compacte d'un montage, pour composer un identifiant dérivé.
 *
 * Volontairement distincte de {@link decrireIdentiteDeMontage} : celle-ci est
 * faite pour être LUE par un humain ou un modèle, celle-là pour être
 * CONCATÉNÉE. Sur la forme legacy elle rend le `documentSessionId` nu, ce qui
 * garde inchangé le `snapshotToken` recomposé par le courtier pour les clients
 * non renouvelés.
 */
export function cleDeMontage(identite: CanvasMountIdentity): string {
  switch (identite.forme) {
    case 'legacy':
      return identite.documentSessionId;
    case 'conversation':
      return `${identite.conversationId}#${identite.mountEpoch}`;
  }
}

/**
 * Deux identités désignent-elles le MÊME montage ?
 *
 * Deux formes différentes ne se comparent jamais : le backend ne dispose
 * d'aucune table de correspondance `documentSessionId` ↔ `(conversation, époque)`,
 * et en inventer une reviendrait à l'alias que ce module refuse. Un client qui
 * change de forme en cours de route change donc de montage — ce qui est le
 * comportement voulu, puisque seul un remontage (donc un nouveau bundle) peut
 * changer la forme émise.
 *
 * ⚠️ Sur la forme legacy, une époque ABSENTE d'un côté ne prouve rien : les
 * hôtes anciens ne l'annoncent pas. Elle n'est comparée que si les deux côtés
 * la portent, sinon `documentSessionId` seul tranche.
 */
export function memeMontage(a: CanvasMountIdentity, b: CanvasMountIdentity): boolean {
  switch (a.forme) {
    case 'legacy':
      if (b.forme !== 'legacy') return false;
      if (a.documentSessionId !== b.documentSessionId) return false;
      if (a.mountEpoch === undefined || b.mountEpoch === undefined) return true;
      return a.mountEpoch === b.mountEpoch;
    case 'conversation':
      if (b.forme !== 'conversation') return false;
      return a.conversationId === b.conversationId && a.mountEpoch === b.mountEpoch;
  }
}

/**
 * Même question que {@link memeMontage}, mais posée par une garde qui doit
 * **prouver** l'égalité des montages plutôt que ne pas la contredire (#1335).
 *
 * WHY deux fonctions plutôt qu'une : la tolérance de `memeMontage` sur une
 * époque legacy absente est légitime là où l'un des deux côtés vient d'un
 * producteur qui ne l'annonce pas — le courtier compare l'annonce de montage à
 * un reçu de navigateur, et un hôte non renouvelé n'en pose aucune dans ses
 * reçus. Elle ne l'est PAS là où l'époque attendue est certaine : le reçu
 * d'admission (`CanvasAdmissionPreparedPayloadSchema`) impose `mountEpoch` dans
 * ses deux formes, donc la fence de `RedactionJobCollector` sait toujours quelle
 * époque elle a admise. Y accepter un signal muet sur l'époque rouvrirait
 * exactement le trou que #1239 avait fermé : la fin du flux d'un montage
 * PRÉCÉDENT rendrait la main au collector du montage courant.
 *
 * Une époque absente d'un côté ou de l'autre rend donc `false` : « je ne peux
 * pas prouver que c'est le même montage » et « ce n'est pas le même montage »
 * mènent ici à la même décision, refuser.
 */
export function memeMontageEpoqueExigee(a: CanvasMountIdentity, b: CanvasMountIdentity): boolean {
  if (a.mountEpoch === undefined || b.mountEpoch === undefined) return false;
  // Les deux époques étant présentes, `memeMontage` les compare déjà.
  return memeMontage(a, b);
}

/**
 * Pose une identité de montage sur une trame sortante, dans la forme que ce
 * montage a annoncée — l'inverse exact de {@link lireIdentiteDeMontage}.
 *
 * Les deux sens de la conversion vivent dans le même module DÉLIBÉRÉMENT : sans
 * cet export, chaque émetteur réimplémentait l'aiguillage à la main (courtier,
 * chargement de document, collector, fence des tools canvas), et une troisième
 * forme ajoutée un jour aurait dû être retrouvée dans quatre fichiers. Ici, le
 * `switch` sans `else` casse la compilation en un seul endroit.
 *
 * ⚠️ **Aucune traduction.** La forme rendue est celle de l'identité reçue : un
 * client non renouvelé qui recevrait la forme conversation ne saurait pas router
 * la commande vers son document.
 */
export function serialiserIdentiteDeMontage(identite: CanvasMountIdentity):
  | { documentSessionId: string; mountEpoch?: number }
  | { conversationId: string; mountEpoch: number } {
  switch (identite.forme) {
    case 'legacy':
      return {
        documentSessionId: identite.documentSessionId,
        ...(identite.mountEpoch !== undefined ? { mountEpoch: identite.mountEpoch } : {}),
      };
    case 'conversation':
      return { conversationId: identite.conversationId, mountEpoch: identite.mountEpoch };
  }
}
